import React from "react";
import { useMemo } from "react";
import * as H from "history";
import {
  FormSchema,
  FormData as GrafferFormData,
} from "@graffer-inc/form-schema";
import {
  PdfFormBuilder,
  PdfFormBuilderSchema,
} from "@graffer-inc/pdf-form-builder";
import { PdfUploaderModal } from "../../../service-provider/modal/pdf-uploader-modal/pdf-uploader-modal";

import { connectService } from "@graffer-inc/smart-apply";
import {
  PdfOutputConfigurationContextProps,
  PdfOutputConfigurationService,
} from "../../../../../application/service/smart-apply/procedure-detail/pdf-output-configuration-service";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { useModal } from "../../../../../utils/custom-hooks";
import { Auth } from "../../../../../application/domain/auth/auth";
import { PreviewFunctionFieldTemplateRegistry } from "../../../../helpers/form-ext/registry/smart-apply-preview-function-field-template-registry";

// NOTE: https://graffer.atlassian.net/browse/PRODUCTS-13457
// graffer-schemasをv31.1.0にアップデートすると、react-datepickerが"locale must contain localize property"のエラーを吐くので、意図的に設定を加えている
import { registerLocale } from "react-datepicker";
import { ja } from "date-fns/locale";
import { corporationSearchFormBaseUrl } from "../../../../../utils/corporate-search/base-url";
import { addressSearchBaseUrl } from "../../../../../utils/postal-code/base-url";
import { formMarkdownProcessorFactory } from "../../../../../utils/markdown/processor";
import {
  grafferSchemasSuportTagNames,
  MarkdownProcessor,
} from "@graffer-inc/form-generator";
registerLocale("ja", ja);

const processor = formMarkdownProcessorFactory(grafferSchemasSuportTagNames);
const smartApplyGrafferSchemasMarkdownProcessor: MarkdownProcessor = (
  content,
) => {
  return processor.processSync(content).result as React.ReactNode;
};

const PdfOutputConfiguration: React.FC<PdfOutputConfigurationContextProps> = ({
  procedure,
  onClickLeave,
  getTenantProcessResultSchema,
  updateSchemas,
  outputSamplePdf,
  removePdfOutputSetting,
  updateOutputBasePDFBinary,
  hasPermissionToDelete,
}) => {
  const uploadPDFModal = useModal();
  const formSubmissionContext = useMemo(() => {
    return {
      submissionDate: new Date(),
    };
  }, []);
  const functionFieldTemplateRegistry = useMemo(() => {
    return new PreviewFunctionFieldTemplateRegistry(
      procedure.getLiveDetail().applicantSchemaKeys,
    );
  }, [procedure]);

  const file = procedure.outputBasePDFBinaryURL;
  const schemas: PdfFormBuilderSchema[] = [
    {
      id: "Application",
      name: "申請項目",
      file,
      uploadPdf: uploadPDFModal.open,
      initialSchema: procedure.draft.template.formSchema,
      downloadPdf: () => window.open(file, "_blank"),
      downloadPdfWithSample: async (formData: GrafferFormData) =>
        outputSamplePdf(formData, "Application"),
    },
    {
      id: "Meta",
      name: "自動取得情報",
      file,
      uploadPdf: uploadPDFModal.open,
      downloadPdf: () => window.open(file, "_blank"),
      // TODO procedure.metaInfoSchema()からIFormSchemaV2型を返すようにする。 [詳細] https://github.com/graffer-inc/cloud-gov/pull/9198#discussion_r946495139
      initialSchema: FormSchema.fromJSON(procedure.metaInfoSchema()),
      downloadPdfWithSample: async (formData: GrafferFormData) =>
        outputSamplePdf(formData, "Meta"),
    },
  ];
  if (procedure.draft.template.tenantProcessResultSetting) {
    schemas.push({
      id: "TenantProcessResult",
      name: "管理情報",
      file,
      uploadPdf: uploadPDFModal.open,
      downloadPdf: () => window.open(file, "_blank"),
      initialSchema: getTenantProcessResultSchema(),
      downloadPdfWithSample: async (formData: GrafferFormData) =>
        outputSamplePdf(formData, "TenantProcessResult"),
    });
  }

  return (
    <>
      <PdfFormBuilder
        schemas={schemas}
        onSave={updateSchemas}
        leaveButtonProps={{
          icon: "back",
          text: "手続き詳細へ戻る",
          onClick: onClickLeave,
        }}
        formSubmissionContext={formSubmissionContext}
        externalServiceUrlConfig={{
          corporationSearchFormBaseUrl: corporationSearchFormBaseUrl,
          addressSearchBaseUrl: addressSearchBaseUrl,
        }}
        headerTitle={procedure.name}
        registries={{
          FunctionFieldTemplateRegistry: functionFieldTemplateRegistry,
        }}
        markdownConfig={{
          markdownProcessor: smartApplyGrafferSchemasMarkdownProcessor,
        }}
        onDelete={removePdfOutputSetting}
        deletable={hasPermissionToDelete()}
      />
      <PdfUploaderModal
        uploadPDFModal={uploadPDFModal}
        procedure={procedure}
        updateOutputBasePDFBinary={updateOutputBasePDFBinary}
      />
    </>
  );
};

export const PdfOutputConfigurationContainer = connectService<
  { procedure: ProcedureEntity; auth: Auth; history: H.History },
  PdfOutputConfigurationContextProps,
  PdfOutputConfigurationService
>(
  PdfOutputConfiguration,
  (arg) =>
    new PdfOutputConfigurationService({
      ...arg,
    }),
);
